// src/components/outputs/pieces/PieceRow.jsx — one non-primary piece of a history card.
//
// Label (SectionLabel in the channel colour) + destination (muted, where the
// piece goes), the value, the counter and a «Copia» button. The value is drawn
// by kind:
//   tags     TagChips of the piece's one paragraph ("#a #b" or "a, b"), as is
//   source   a collapsed <details> («Mostra HTML») holding a monospace <pre>
//            of the verbatim source (the blog's body_html)
//   others   BlocksView (plain / rich / url / list)
// PieceValue is exported so ReadyToPublish draws its primary piece the same way.
//
// Copy: onCopy(pieceText(piece), "piece-<entryId>-<pieceId>", { html }) where
// html is pieceHtml(piece) for a 'rich' piece and null otherwise — a plain box
// must not receive markup; a source piece's text IS the markup. The same id is
// the DOM id of the «Copia» button (CopyBtn forwards it), so every copy control
// of a card is addressable and unique. «Copiato» shows while copiedId equals
// that id. The counter counts pieceText (code points, cites dropped) against
// piece.limit.
// A hand selection inside the value box copies clean (cleanCopy.js ::
// onCopyWithinBox on the box div): allowlisted tags, no styles, no box.
//
//   PieceRow({ piece, entryId, copiedId, onCopy, t, w, color })

import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_MONO, RADII } from '../../../constants/theme.js';
import { pieceText, pieceHtml, pieceLength } from '../../../publish/pieces/index.js';
import { SectionLabel, TagChips, CopyBtn } from '../OutputPrimitives.jsx';
import BlocksView from './BlocksView.jsx';
import PieceCounter from './PieceCounter.jsx';
import { onCopyWithinBox } from './cleanCopy.js';

const defaultW = (o) => `rgba(255,255,255,${o})`;

/** The id the copy button of a piece reports to onCopy / matches copiedId with. */
export const pieceCopyId = (entryId, piece) => `piece-${entryId}-${piece?.id}`;

/** The html payload of a copy: only a rich piece carries one. */
export const copyHtmlOf = (piece) => (piece?.kind === 'rich' ? pieceHtml(piece) : null);

/** "#a #b" → ['#a', '#b']; "a, b" → ['a', 'b'] (the text already says what the box takes). */
export const tagTokens = (text) => {
  const s = String(text || '').trim();
  if (!s) return [];
  return (s.includes(',') ? s.split(',') : s.split(/\s+/)).map((x) => x.trim()).filter(Boolean);
};

/** The value of a piece, drawn by kind (shared by PieceRow and ReadyToPublish). */
export const PieceValue = ({ piece, t = {}, w = defaultW, color, compact }) => {
  const { t: tr } = useTranslation('outputs');
  if (!piece) return null;
  const accent = color || COLORS.accent;

  if (piece.kind === 'tags') {
    const text = pieceText(piece);
    return (
      <TagChips
        tags={tagTokens(text)}
        color={accent}
        prefix=""
        separator={text.includes(',') ? ', ' : ' '}
        style={{ marginBottom: 0 }}
      />
    );
  }

  if (piece.kind === 'source') {
    return (
      <details data-testid="piece-source" style={{ fontSize: '12px', color: t.text }}>
        <summary style={{ cursor: 'pointer', color: t.textMuted || COLORS.textTertiary, fontWeight: 600 }}>
          {tr('pieces.ui.showSource')}
        </summary>
        <pre
          style={{
            margin: '8px 0 0',
            padding: '12px 14px',
            background: w(0.03),
            border: `1px solid ${t.border || COLORS.border}`,
            borderRadius: RADII.controlInner,
            fontFamily: FONT_MONO,
            fontSize: '11.5px',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowX: 'auto',
          }}
        >
          {pieceText(piece)}
        </pre>
      </details>
    );
  }

  return <BlocksView blocks={piece.blocks} t={t} w={w} color={accent} compact={compact} />;
};

const PieceRow = ({ piece, entryId, copiedId, onCopy, t = {}, w = defaultW, color }) => {
  const { t: tr } = useTranslation('outputs');
  const boxRef = useRef(null);
  if (!piece) return null;
  const id = pieceCopyId(entryId, piece);
  const accent = color || COLORS.accent;

  return (
    <div data-testid={`piece-row-${piece.id}`} style={{ marginBottom: '14px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: '6px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <SectionLabel color={accent} t={t}>{piece.label}</SectionLabel>
          {piece.destination ? (
            <div
              style={{
                fontSize: '11px',
                color: t.textMuted || COLORS.textTertiary,
                marginTop: '-2px',
                marginBottom: '6px',
              }}
            >
              {piece.destination}
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <PieceCounter count={pieceLength(piece)} limit={piece.limit || null} t={t} />
          <CopyBtn
            id={id}
            active={copiedId === id}
            onClick={() => onCopy?.(pieceText(piece), id, { html: copyHtmlOf(piece) })}
            label={tr('common.copy')}
            t={t}
            color={accent}
          />
        </div>
      </div>
      <div
        ref={boxRef}
        data-testid={`piece-box-${piece.id}`}
        onCopy={onCopyWithinBox(boxRef)}
        style={{
          padding: '12px 16px',
          background: w(0.02),
          border: `1px solid ${w(0.06)}`,
          borderRadius: RADII.control,
          color: t.text,
          fontSize: '14px',
          lineHeight: 1.6,
        }}
      >
        <PieceValue piece={piece} t={t} w={w} color={accent} compact />
      </div>
    </div>
  );
};

export default PieceRow;
